import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les faisceaux sont leur propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'harp',
  family: 'stage',
  danceability: 0.60,
  requiresTier: 0,
  energyLevel: 0.80,
  bpmMin: 100,
  bpmMax: 150,
  movement: 0.75,
  tonality: 0.55,
  enabled: true
};

// HARP : une harpe laser. Douze faisceaux en éventail montent d'un émetteur
// posé au sol sous la pochette ; la musique les « pince » : un faisceau est
// coupé à une hauteur (plus bas pour une note forte), le point de coupure
// éclate en étoile, quelques étincelles tombent, et le faisceau se reforme
// du bas vers le haut. Électro, trance, synthwave, électronique mélodique.
//
// LA GÉOMÉTRIE, en unités de hauteur (y de -1 à 1). Un éventail de 70° issu
// d'un POINT sous la pochette traverserait la pochette. Les faisceaux sortent
// donc d'une FENTE le long d'un émetteur allongé, comme d'un sommet virtuel
// placé sous l'écran ; un vide central, calculé à chaque frame sur la zone
// de sécurité de la pochette, sépare les deux demi-éventails (6 + 6
// faisceaux, des graves à gauche aux aigus à droite), qui couvrent ensemble
// 70°. Chaque faisceau s'arrête (fondu de 4 px) avant la pochette, les zones
// d'exclusion et la marge de 5 % : quand l'éventail s'incline ou s'ouvre, un
// faisceau qui y entrerait est coupé, jamais caché derrière.
//
// LE RENDU : tout est calculé par distance dans un shader plein écran
// (R12 : des sources fixes), la boucle sur les 12 faisceaux DÉROULÉE en JS
// (le GPU du Pi lit mal un tableau d'uniforms indexé dans une boucle) :
//   - le faisceau (R1) : cœur blanc chaud de 1 px, gaine colorée de 3 px à
//     luminance 0,7, halo exp(-d/10) x 0,35, et un VOLUME large et faible
//     que seule la fumée rend visible ;
//   - la fumée (R14) : basse, deux échelles (un voile, des filaments),
//     alpha = 1 - exp(-densité x k), sans seuil ; elle n'a aucune lumière
//     propre, elle ne se lit que dans les faisceaux ;
//   - l'impact : un cœur blanc, une lueur, une étoile de diffraction à 4
//     branches de 1 px ;
//   - le sol : miroir analytique des faisceaux, flou (le profil élargi) et
//     atténué avec la distance à la ligne du sol ;
//   - l'émetteur : un boîtier noir, son arête, sa fente qui luit.
// Les étincelles sont des quads instanciés (un draw call), leur trajectoire
// balistique calculée dans le vertex shader depuis l'instant du pincement.
//
// LE RYTHME : croche = pincements selon les bandes (grave à gauche, médium
// au milieu, aigu à droite ; motif arpège montant, descendant ou accord,
// changé à chaque mesure) ; mesure = accord de 3 cordes ; phrase = couleur
// suivante et geste de l'éventail (inclinaison de ±15°, ou fermeture puis
// réouverture) ; montée = glissando de gauche à droite de plus en plus
// rapide ; drop = toutes les cordes coupées ensemble, flash aux impacts, puis
// les 12 se reforment et l'éventail s'ouvre à 100° jusqu'à la phrase
// suivante. Dansabilité 0,60 (R9 : impulsions modérées, aucun flash plein
// écran) : les impacts du drop restent locaux et à 75 %. Sans BPM : une
// horloge interne à 120.
//
// DEBUG : ?harppluck=<0..11> (cette corde seule, à chaque temps),
// ?harpfan=<deg> (ouverture totale forcée), ?harpprobe=1 (window.__harp).

const N = 12;
const FAN_DEG = 70, DROP_FAN_DEG = 100;
const FLOOR_Y = -0.72;              // the slit, in height units
const BOX_H = 0.07;
const SLIT_HALF = 0.92;             // the slit's half length (clear of the bottom-right exclusion)
const SAFE_MARGIN = 0.05;
const EDGE_MARGIN = 0.10;
const EXCLUSIONS = [['tr', 0.22, 0.12], ['tl', 0.08, 0.12], ['br', 0.22, 0.10]];
const CUT_HOLD_BEATS = 0.25;
const REFORM_S = 0.06;
const SPARKS = 10;
const NO_BPM = 120;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const SHEATH_LUM = 0.70;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- la géométrie de l'éventail ------------------------------------------------------
// The boxes a beam must stop before: the cover's safe zone (+ margin) and
// the three exclusion zones, in height units.
export function obstacles(aspect, sc, sh) {
  const A = aspect, boxes = [];
  boxes.push([sc[0] - sh[0] - SAFE_MARGIN, sc[1] - sh[1] - SAFE_MARGIN, sc[0] + sh[0] + SAFE_MARGIN, sc[1] + sh[1] + SAFE_MARGIN]);
  for (const [corner, fw, fh] of EXCLUSIONS) {
    const w = fw * 2 * A, h = fh * 2;
    const x0 = corner[1] === 'r' ? A - w : -A, x1 = corner[1] === 'r' ? A : -A + w;
    const y0 = corner[0] === 't' ? 1 - h : -1, y1 = corner[0] === 't' ? 1 : -1 + h;
    boxes.push([x0 - 0.02, y0 - 0.02, x1 + 0.02, y1 + 0.02]);
  }
  return boxes;
}
// the length of a ray from o along d until it leaves the frame (minus the
// margin) or enters a box
export function rayLength(o, d, aspect, boxes) {
  let t = 1e9;
  const xm = aspect - EDGE_MARGIN, ym = 1 - EDGE_MARGIN;
  if (d[1] > 1e-6) t = Math.min(t, (ym - o[1]) / d[1]);
  if (d[0] > 1e-6) t = Math.min(t, (xm - o[0]) / d[0]);
  if (d[0] < -1e-6) t = Math.min(t, (-xm - o[0]) / d[0]);
  for (const b of boxes) {
    // slab intersection; only boxes ahead of the origin
    let t0 = -1e9, t1 = 1e9;
    for (let k = 0; k < 2; k++) {
      const lo = b[k], hi = b[k + 2];
      if (Math.abs(d[k]) < 1e-9) { if (o[k] < lo || o[k] > hi) { t0 = 1e9; break; } }
      else { let a = (lo - o[k]) / d[k], c = (hi - o[k]) / d[k]; if (a > c) { const s = a; a = c; c = s; } t0 = Math.max(t0, a); t1 = Math.min(t1, c); }
    }
    if (t0 <= t1 && t1 > 0) t = Math.min(t, Math.max(0, t0));
  }
  return Math.max(0, t);
}
// The 12 beams: origins on the slit (fixed, from the 70° layout and the
// virtual apex), angles opened by `spread` (half-fan, degrees) and tilted.
// The inner gap clears the cover's safe zone as seen from the apex.
export function fanLayout(aspect, sc, sh, spreadDeg, tiltDeg) {
  const half = FAN_DEG / 2;
  const apexY = FLOOR_Y - SLIT_HALF / Math.tan(half * Math.PI / 180);
  // the gap: the steepest angle from the apex to the safe zone's corners
  const corners = [[sc[0] + sh[0] + SAFE_MARGIN, sc[1] - sh[1] - SAFE_MARGIN], [sc[0] + sh[0] + SAFE_MARGIN, sc[1] + sh[1] + SAFE_MARGIN]];
  let gap = 0;
  for (const c of corners) gap = Math.max(gap, Math.atan2(Math.abs(c[0]), c[1] - apexY) * 180 / Math.PI);
  gap = Math.min(gap + 1.5, half - 6);
  const beams = [];
  const boxes = obstacles(aspect, sc, sh);
  for (let i = 0; i < N; i++) {
    const side = i < 6 ? -1 : 1, k = i < 6 ? 5 - i : i - 6;       // 0 = innermost
    const base = gap + (half - gap) * k / 5;                        // degrees from vertical, at 70°
    const ox = (FLOOR_Y - apexY) * Math.tan(base * Math.PI / 180) * side;
    // opening: the angle scales with the spread; tilt: added to all
    const ang = (side * base * (spreadDeg / half) + tiltDeg) * Math.PI / 180;
    const d = [Math.sin(ang), Math.cos(ang)];
    const o = [ox, FLOOR_Y];
    const L = rayLength(o, d, aspect, boxes);
    beams.push({ o, d, L, base, side });
  }
  return { beams, gap, apexY };
}

// --- GLSL -----------------------------------------------------------------------------
const QUAD_VERTEX = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// one beam (constant index i): its contribution at p, in the main pass and
// in the floor's mirror (wide = 1: the blurred profile)
function beamGLSL(i) {
  return `
    {
      vec4 A = uBeamA[${i}];            // origin x, y, direction x, y
      vec4 B = uBeamB[${i}];            // length, visible top, gain, -
      vec2 v = p - A.xy;
      float along = dot(v, A.zw);
      // a CAPSULE from the slit to the visible top (cut or whole): every
      // term rounds off at the end instead of stopping on a straight edge
      float t = clamp(along, 0.0, B.y);
      float dpx = length(v - A.zw * t) * uPxPerUnit;
      float topFade = 1.0 - 0.8 * smoothstep(0.30, 1.0, t / max(B.x, 1e-3));
      float g = topFade * B.z * step(0.0005, B.y);
      if (g > 0.0005 && dpx < 90.0) {
        // R1: a core that never gets thinner than a pixel (no beading),
        // the sheath at 0.7, the halo; the volume only in the smoke
        float core = 1.0 - smoothstep(0.0, 1.6, dpx * uWide);
        float sheath = 1.0 - smoothstep(1.4, 2.8, dpx * uWide);
        float halo = 0.35 * exp(-dpx * uWide / 10.0);
        float vol = exp(-dpx / 16.0) * 0.45 * smoke;
        lum += g * core * uCore;
        col += g * (max(sheath, halo) + vol);
      }
    }`;
}
function impactGLSL(i) {
  return `
    {
      vec4 I = uImp[${i}];              // x, y, strength, -
      if (I.z > 0.003) {
        vec2 q = (p - I.xy) * uPxPerUnit;
        float r2 = dot(q, q);
        float s = I.z;
        float core = exp(-r2 / 8.0) * s;
        float glow = 0.22 * exp(-sqrt(r2) / 7.0) * s;
        float sp = 6.0 + 14.0 * s;
        float bx = exp(-abs(q.x) / sp) * max(0.0, 1.0 - abs(q.y));
        float by = exp(-abs(q.y) / sp) * max(0.0, 1.0 - abs(q.x));
        lum += core * 1.6 + (bx + by) * 0.7 * s;
        col += glow + (bx + by) * 0.4 * s;
      }
    }`;
}
let beamsBody = '', impactsBody = '';
for (let i = 0; i < N; i++) { beamsBody += beamGLSL(i); impactsBody += impactGLSL(i); }

const HARP_FRAGMENT = `
  precision highp float;
  uniform vec4 uBeamA[${N}];
  uniform vec4 uBeamB[${N}];
  uniform vec4 uImp[${N}];
  uniform float uAspect;
  uniform float uPxPerUnit;
  uniform vec3 uColor;        // the sheath (luminance 0.7)
  uniform float uTime;
  uniform float uSmokeOct;
  uniform float uSlitHalf;
  varying vec2 vUv;
  // value noise on a bounded lattice (fp16 on the TV)
  float hash(vec2 q) { q = mod(q, 289.0); return fract(sin(dot(q, vec2(12.9898, 78.233))) * 43758.5453); }
  float vnoise(vec2 q) {
    vec2 i = floor(q), f = fract(q);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  // R14: low smoke, a light veil and denser filaments; the smoke DRIFTS (its
  // frame moves), the warp stays under half the noise's wavelength
  float smokeAt(vec2 p) {
    vec2 q = p + vec2(uTime * 0.025, 0.0);
    float veil = vnoise(q * 1.3) * 0.6 + vnoise(q * 2.7 + 7.0) * 0.4;
    vec2 w = vec2(vnoise(q * 3.1 + 3.0), vnoise(q * 3.1 + 11.0)) - 0.5;
    float fil = vnoise(q * 6.2 + w * 0.9);
    if (uSmokeOct > 1.5) fil = fil * 0.65 + vnoise(q * 12.5 + w * 0.5) * 0.35;
    float h = exp(-max(p.y - ${FLOOR_Y.toFixed(2)}, 0.0) / 0.55);          // low smoke
    float density = h * (0.55 * veil + 0.75 * smoothstep(0.45, 0.85, fil));
    return 1.0 - exp(-density * 1.6);                                      // translucent, no threshold
  }
  vec3 beams(vec2 p, float uWide, float uCore, float smoke, out float lumOut) {
    float lum = 0.0, col = 0.0;
    ${beamsBody}
    lumOut = lum;
    return vec3(col);
  }
  void main() {
    vec2 p = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    float smoke = smokeAt(p);
    float lum;
    vec3 c3 = beams(p, 1.0, 1.0, smoke, lum);
    float col = c3.x;
    ${impactsBody}
    vec3 c = uColor * min(col, 1.6) + vec3(1.0, 0.93, 0.85) * min(lum, 2.0);
    // the floor: a blurred analytic mirror of the beams, fading with depth
    if (p.y < ${FLOOR_Y.toFixed(2)} - ${BOX_H.toFixed(2)}) {
      vec2 m = vec2(p.x, 2.0 * ${FLOOR_Y.toFixed(2)} - p.y);
      float l2;
      vec3 r = beams(m, 0.35, 0.0, 0.0, l2);
      float depth = ${FLOOR_Y.toFixed(2)} - p.y;
      c += uColor * r.x * 0.22 * exp(-depth / 0.12);
    }
    // the emitter: a black box, a thin edge catching the light, the slit
    vec2 b = p - vec2(0.0, ${(FLOOR_Y - BOX_H / 2).toFixed(3)});
    vec2 bh = vec2(uSlitHalf + 0.06, ${(BOX_H / 2).toFixed(3)});
    vec2 dq = abs(b) - bh;
    float sd = length(max(dq, 0.0)) + min(max(dq.x, dq.y), 0.0);
    float px = 1.0 / uPxPerUnit;
    float inside = 1.0 - smoothstep(-0.5 * px, 0.5 * px, sd);
    float edge = (1.0 - smoothstep(0.0, 1.2 * px, abs(p.y - ${FLOOR_Y.toFixed(2)}))) * step(abs(p.x), bh.x);
    float slit = (1.0 - smoothstep(0.0, 1.5 * px, abs(p.y - ${(FLOOR_Y - 0.012).toFixed(3)}))) * step(abs(p.x), uSlitHalf);
    c = mix(c, vec3(0.006), inside * 0.92);
    c += vec3(0.07) * edge + uColor * slit * 0.35;
    gl_FragColor = vec4(c, 1.0);
  }
`;

// the sparks: instanced quads, a ballistic fall from the pluck's instant
const SPARK_VERTEX = `
  precision highp float;
  attribute float aBeam;
  attribute vec3 aSeed;
  uniform vec4 uSpark[${N}];   // x, y, start time (s), strength
  uniform float uTime;
  uniform float uAspect;
  uniform float uPxPerUnit;
  varying vec2 vLoc;
  varying float vFade;
  void main() {
    vec4 S = uSpark[int(aBeam + 0.5)];
    float t = uTime - S.z;
    float life = 0.35 + 0.45 * aSeed.z;
    if (t < 0.0 || t > life || S.w < 0.05) { gl_Position = vec4(0.0); vFade = 0.0; return; }
    vec2 v = vec2((aSeed.x - 0.5) * 0.9, 0.15 + 0.35 * aSeed.y) * (0.6 + 0.6 * S.w);
    vec2 pos = S.xy + v * t + vec2(0.0, -1.7) * t * t * 0.5;
    float R = 8.0;                                      // 6 x the glow's width: 0 before the edge (R12)
    vLoc = position.xy * R;
    vFade = (1.0 - t / life) * S.w;
    vec2 q = pos + vLoc / uPxPerUnit;
    gl_Position = vec4(q.x / uAspect, q.y, 0.0, 1.0);
  }
`;
const SPARK_FRAGMENT = `
  precision mediump float;
  uniform vec3 uColor;
  varying vec2 vLoc;
  varying float vFade;
  void main() {
    float r = length(vLoc);
    float core = exp(-r * r / 1.2);
    float glow = 0.25 * exp(-r / 1.3);
    vec3 c = (vec3(1.0, 0.93, 0.85) * core + uColor * glow) * vFade;
    gl_FragColor = vec4(c, 1.0);
  }
`;

function sparkGeometry() {
  const n = N * SPARKS;
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  const beam = new Float32Array(n), seed = new Float32Array(n * 3);
  let s = 0x2545f491;
  const rnd = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  for (let i = 0; i < n; i++) { beam[i] = Math.floor(i / SPARKS); seed[i * 3] = rnd(); seed[i * 3 + 1] = rnd(); seed[i * 3 + 2] = rnd(); }
  g.setAttribute('aBeam', new THREE.InstancedBufferAttribute(beam, 1));
  g.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 3));
  g.instanceCount = n;
  return g;
}

// the harp's colours: vivid, one per phrase, at the sheath's luminance
function hueDist(a, b) { const d = Math.abs(a - b) % 1; return Math.min(d, 1 - d); }
export function harpColours(palette) {
  const hsl = { h: 0, s: 0, l: 0 }, cand = [];
  if (palette) for (const k of ['vividColors', 'dominantColors']) for (const c of (palette[k] || [])) { new THREE.Color(c).getHSL(hsl); cand.push({ c, h: hsl.h, s: hsl.s }); }
  cand.sort((a, b) => b.s - a.s);
  const out = [];
  for (const k of cand) { if (out.length >= 3) break; if (out.every((x) => hueDist(x.h, k.h) >= 0.25)) out.push(k); }
  const h0 = out.length ? out[0].h : 0.33;
  for (const dh of [0, 1 / 3, 2 / 3, 0.5]) { if (out.length >= 3) break; const h = (h0 + dh) % 1; if (out.every((x) => hueDist(x.h, h) >= 0.25)) out.push({ c: new THREE.Color().setHSL(h, 1, 0.5).getHex(), h, s: 1 }); }
  return out.map((x) => {
    const o = vdjColor(x.c, new THREE.Color(), 0.95);
    const lum = 0.2126 * o.r + 0.7152 * o.g + 0.0722 * o.b, mx = Math.max(o.r, o.g, o.b);
    return o.multiplyScalar(Math.min(1 / Math.max(mx, 1e-4), SHEATH_LUM / Math.max(lum, 1e-4)));
  });
}

export class HarpVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const pl = parseInt(urlParam('harppluck'), 10);
    this._forcePluck = Number.isFinite(pl) && pl >= 0 && pl < N ? pl : null;
    const fd = parseFloat(urlParam('harpfan'));
    this._forceFan = Number.isFinite(fd) && fd > 5 ? Math.min(160, fd) : null;
    this._post = { bloomStrength: 0.10, bloomThreshold: 0.78, grain: 0.01, vignette: 0.30, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('harpprobe') === '1' && typeof window !== 'undefined') window.__harp = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    const v4s = () => Array.from({ length: N }, () => new THREE.Vector4());
    this._u = {
      uBeamA: { value: v4s() }, uBeamB: { value: v4s() }, uImp: { value: v4s() },
      uAspect: { value: 1.78 }, uPxPerUnit: { value: 540 }, uColor: { value: new THREE.Color(0, 1, 0) },
      uTime: { value: 0 }, uSmokeOct: { value: this._tier >= 1 ? 2 : 1 }, uSlitHalf: { value: SLIT_HALF }
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._u, vertexShader: QUAD_VERTEX, fragmentShader: HARP_FRAGMENT, depthTest: false, depthWrite: false
    }));
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) {
      // px per height unit: from the target actually drawn (the composer's,
      // at the tier's scale), not the canvas
      const cur = renderer.getRenderTarget();
      const h = cur ? cur.height : renderer.getDrawingBufferSize(_size).y;
      self._u.uPxPerUnit.value = h / 2;
    };
    scene.add(this._quad);
    this._sparkU = { uSpark: { value: v4s() }, uTime: this._u.uTime, uAspect: this._u.uAspect, uPxPerUnit: this._u.uPxPerUnit, uColor: this._u.uColor };
    this._sparks = new THREE.Mesh(sparkGeometry(), new THREE.ShaderMaterial({
      uniforms: this._sparkU, vertexShader: SPARK_VERTEX, fragmentShader: SPARK_FRAGMENT,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    }));
    this._sparks.frustumCulled = false;
    this._sparks.renderOrder = -999;
    scene.add(this._sparks);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._clock = 0;               // beats
    this._beatIdx = 0; this._bpPrev = null; this._barPrev = null; this._phPrev = null;
    this._eighth = -1; this._barNo = 0; this._phraseNo = 0;
    this._cut = Array.from({ length: N }, () => ({ h: 1, until: -1, reform: -1, imp: 0, impT: -9 }));
    this._spread = FAN_DEG / 2; this._tilt = 0;
    this._gesture = null;          // { kind, t0, t1, from, to }
    this._dropOpen = false;
    this._build = null;            // glissando: { start, cursor }
    this._modePrev = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteSeen = undefined; this._cols = harpColours(null); this._colIdx = 0;
    this._u.uColor.value.copy(this._cols[0]);
    this._seed = 0x9e3779b9;
    this._log = [];
  }

  _rand() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }
  _note(what, extra) { this._log.push(Object.assign({ t: +this._time.toFixed(3), what, beat: +this._clock.toFixed(2) }, extra || {})); if (this._log.length > 400) this._log.shift(); }

  // a pluck: the beam cut at a height (lower for a louder note), the impact,
  // the sparks; the cut holds 1/4 beat, then the beam regrows in 60 ms
  _pluck(i, strength, beatS) {
    const c = this._cut[i];
    const s = clamp01(strength);
    c.h = Math.max(0.12, Math.min(0.9, 0.80 - 0.55 * s + (this._rand() - 0.5) * 0.10));
    c.until = this._time + CUT_HOLD_BEATS * beatS;
    c.reform = c.until + REFORM_S;
    c.imp = 0.55 + 0.45 * s; c.impT = this._time;
    this._sparkPending = this._sparkPending || [];
    this._sparkPending.push(i);
    this._note('pluck', { i, s: +s.toFixed(2) });
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    this._u.uAspect.value = aspect;
    if (profile.artworkPalette !== this._paletteSeen) {
      this._paletteSeen = profile.artworkPalette;
      this._cols = harpColours(profile.artworkPalette);
      this._u.uColor.value.copy(this._cols[this._colIdx % this._cols.length]);
    }
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;            // frozen: the cuts, the sparks and the fan hold
    this._time += dt;
    this._u.uTime.value = this._time;

    // THE CLOCK, in beats (an internal one without a grid)
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    const grid = bp != null && bpm > 0;
    const tempo = grid ? bpm : NO_BPM;
    const beatS = 60 / tempo;
    let barEdge = false, phraseEdge = false;
    if (grid) {
      if (this._bpPrev != null && bp < this._bpPrev - 0.5) this._beatIdx++;
      this._bpPrev = bp;
      this._clock = this._beatIdx + bp;
      if (barIn != null) { if (this._barPrev != null && barIn < this._barPrev - 0.5) barEdge = true; this._barPrev = barIn; }
      if (ph != null) { if (this._phPrev != null && ph < this._phPrev - 0.5) phraseEdge = true; this._phPrev = ph; }
    } else {
      this._clock += dt / beatS;
      this._bpPrev = null; this._barPrev = null; this._phPrev = null;
    }
    const e8 = Math.floor(this._clock * 2);
    const eighthEdge = e8 !== this._eighth;
    if (eighthEdge) {
      const prev = this._eighth;
      this._eighth = e8;
      if (!grid && prev >= 0) {
        if (e8 % 8 === 0) barEdge = true;
        if (e8 % 64 === 0) phraseEdge = true;
      }
    }
    if (barEdge) this._barNo++;

    // THE STRUCTURE: build (glissando), drop
    const dir = profile.director || {};
    const mode = dir.mode || null;
    const drop = (grid && this._tickDrops(profile)) || (mode === 'drop' && this._modePrev != null && this._modePrev !== 'drop');
    if (mode === 'build' && this._modePrev !== 'build') this._build = { start: this._clock, next: this._clock, cursor: 0 };
    if (mode !== 'build') this._build = null;
    this._modePrev = mode;

    // THE PHRASE: the next colour, a gesture of the fan
    if (phraseEdge) {
      this._phraseNo++;
      this._colIdx++;
      this._u.uColor.value.copy(this._cols[this._colIdx % this._cols.length]);
      this._dropOpen = false;
      const kind = this._phraseNo % 2 === 1 ? 'tilt' : 'breathe';
      const sign = this._phraseNo % 4 === 1 ? 1 : -1;
      this._gesture = { kind, t0: this._clock, sign };
      this._note('phrase', { kind, colour: this._colIdx % this._cols.length });
    }

    // THE PLUCKS
    const bands = dir.bands || {};
    let lo = bands.low || 0, mi = bands.mid || 0, hi = bands.high || 0;
    if (lo + mi + hi < 0.01) {
      // no analysis: a gentle synthetic arrangement on the eighths
      const k = ((this._eighth % 8) + 8) % 8;
      lo = k % 4 === 0 ? 0.75 : 0.25; mi = k % 2 === 1 ? 0.6 : 0.2; hi = k % 4 === 2 ? 0.55 : 0.3;
    }
    if (drop) {
      // every string cut at once, a flash at the impacts; they reform together
      // R9 at 0.60: moderate pulses, no flash - the impacts stay local and
      // under full strength
      for (let i = 0; i < N; i++) this._pluck(i, 0.75, beatS);
      for (const c of this._cut) c.h = 0.42 + (this._rand() - 0.5) * 0.06;
      this._dropOpen = true;
      this._gesture = null;
      this._note('drop');
    } else if (this._forcePluck != null) {
      if (eighthEdge && this._eighth % 2 === 0) this._pluck(this._forcePluck, 0.7, beatS);
    } else if (this._build) {
      // the glissando: left to right, faster and faster (1/2 beat to 1/8)
      const prog = clamp01((this._clock - this._build.start) / 32);
      const step = 0.5 - 0.375 * prog;
      while (this._clock >= this._build.next) {
        this._pluck(this._build.cursor % N, 0.4 + 0.5 * prog, beatS);
        this._build.cursor++;
        this._build.next += step;
      }
    } else if (eighthEdge) {
      const k = ((this._eighth % 8) + 8) % 8;            // the eighth in the bar
      const pattern = this._barNo % 3;                   // 0 up, 1 down, 2 chord
      if (barEdge) {
        // the bar: a chord of three strings
        const r = this._barNo % 4;
        this._pluck(r, 0.85, beatS); this._pluck(4 + r, 0.8, beatS); this._pluck(8 + r, 0.75, beatS);
      } else {
        const groups = [[lo, 0, [0, 3, 4, 7]], [mi, 4, [2, 6]], [hi, 8, [0, 1, 2, 3, 4, 5, 6, 7]]];
        for (const [level, base, when] of groups) {
          if (level < 0.32 || when.indexOf(k) < 0) continue;
          const n = Math.floor(k / 2) + this._barNo;
          if (pattern === 0) this._pluck(base + (n % 4), level, beatS);
          else if (pattern === 1) this._pluck(base + 3 - (n % 4), level, beatS);
          else if (k % 4 === 0) { this._pluck(base, level, beatS); this._pluck(base + 2, level * 0.9, beatS); this._pluck(base + 3, level * 0.8, beatS); }
        }
      }
    }

    // THE FAN: the gesture of the phrase, the drop's opening, the debug
    let spread = FAN_DEG / 2, tilt = 0;
    if (this._gesture) {
      const g = this._gesture, t = this._clock - g.t0;
      if (g.kind === 'tilt') tilt = 15 * g.sign * smooth01(t / 8);
      else spread = FAN_DEG / 2 - 13 * (t < 4 ? smooth01(t / 4) : 1 - smooth01((t - 4) / 4));
    }
    if (this._dropOpen) spread = DROP_FAN_DEG / 2;
    if (this._forceFan != null) spread = this._forceFan / 2;
    this._spread += (spread - this._spread) * Math.min(1, dt / 0.35);
    this._tilt += (tilt - this._tilt) * Math.min(1, dt / 0.35);

    // the beams' uniforms (R19: the fan is laid out around the stage centre)
    const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;
    const lay = fanLayout(aspect, [sc.x, sc.y], [sh.x, sh.y], this._spread, this._tilt);
    this._layout = lay;
    for (let i = 0; i < N; i++) {
      const b = lay.beams[i], c = this._cut[i];
      let top = b.L;
      if (this._time < c.until) top = b.L * c.h;
      else if (this._time < c.reform) top = b.L * (c.h + (1 - c.h) * smooth01((this._time - c.until) / REFORM_S));
      this._u.uBeamA.value[i].set(b.o[0], b.o[1], b.d[0], b.d[1]);
      this._u.uBeamB.value[i].set(b.L, top, 1, 0);
      // the impact at the cut, while the beam is cut
      const t = this._time - c.impT;
      const imp = this._time < c.reform ? c.imp * (0.35 + 0.65 * Math.exp(-t / 0.08)) : 0;
      const ix = b.o[0] + b.d[0] * b.L * c.h, iy = b.o[1] + b.d[1] * b.L * c.h;
      this._u.uImp.value[i].set(ix, iy, imp, 0);
    }
    if (this._sparkPending) {
      for (const i of this._sparkPending) {
        const b = lay.beams[i], c = this._cut[i];
        this._sparkU.uSpark.value[i].set(b.o[0] + b.d[0] * b.L * c.h, b.o[1] + b.d[1] * b.L * c.h, this._time, c.imp);
      }
      this._sparkPending = null;
    }
  }

  dispose() {
    for (const m of [this._quad, this._sparks]) {
      if (!m) continue;
      m.onBeforeRender = null;
      if (this._scene) this._scene.remove(m);
      m.geometry.dispose(); m.material.dispose();
    }
    if (typeof window !== 'undefined' && window.__harp === this) window.__harp = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
